import React, { useState, useEffect } from "react";
import { MapRef } from "react-map-gl";

import {
  TextField,
  Autocomplete,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import SearchIcon from "@material-ui/icons/Search";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import { latLngIsInItaly } from "../util/MapUtilsTS";
import { useClientConfig } from "../../context/clientConfigContext";

interface Suggestion {
  id: string;
  placeName: string;
  center: [number, number]; // [latitude, longitude]
  properties?: {
    [key: string]: any;
  };
  isCoordinate?: boolean;
}

interface CustomGeocoderProps {
  mapRef: React.RefObject<MapRef>;
  onViewStateChange?: (viewState: PartialViewState) => void;
  handleOnResult?: (location: string) => void;
  country?: string;
  placeholder?: string;
}
const TOKEN: string = process.env.REACT_APP_MAPBOX_TOKEN!;
const coordPattern = /^[ ]*(-?\d+\.?\d*)[, ]+(-?\d+\.?\d*)[ ]*$/;

export const MapSearchInputNoCountryCheck: React.FC<CustomGeocoderProps> = ({
  mapRef,
  onViewStateChange,
  handleOnResult,
  country = "it", // Default to Italy
  placeholder = "Search by name or coordinates (lat, lng)",
}) => {
  const config = useClientConfig();

  const [input, setInput] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const [coordinatesOutOfRange, setCoordinatesOutOfRange] = useState<boolean>(false);
  const [coordinatesReversed, setCoordinatesReversed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [coordinatesInItaly, setCoordinatesInItaly] = useState<boolean>(true);

  useEffect(() => {
    const noSpaceInput = input.trim();
    setCoordinatesOutOfRange(false);

    if (!input || noSpaceInput === "") {
      setSuggestions([]);

      return;
    }

    const isCoordFormat = coordPattern.test(noSpaceInput);

    if (isCoordFormat) {
      const matches = noSpaceInput.match(coordPattern);
      if (matches) {
        const coord1 = parseFloat(matches[1]);
        const coord2 = parseFloat(matches[2]);

        const coordSuggestion: Suggestion = {
          id: "coordinate-search",
          placeName: `${coord1}, ${coord2}`, // after selection the user needs to see what he initially inserted
          center: [coord1, coord2],
          isCoordinate: true,
        };
        setSuggestions([coordSuggestion]);
      }
    } else {
      // If not coordinates, fetch suggestions from geocoding API
      fetchSuggestions(noSpaceInput);
    }
  }, [input]);

  const fetchSuggestions = async (query: string) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${TOKEN}`,
      );

      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }

      const data = await response.json();

      // Map API response to our Suggestion interface
      const mappedSuggestions: Suggestion[] = data.features.map((feature: any) => ({
        id: feature.id,
        placeName: feature.place_name,
        // mapbox puts the longitude first and latitude second, users insert coords the opposite way
        center: [feature.center[1], feature.center[0]],
        properties: feature.properties,
      }));

      // Add custom location to suggestions
      setSuggestions([...mappedSuggestions]);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (rawSuggestion: string | Suggestion | null) => {
    if (!rawSuggestion) return;
    let suggestion: Suggestion | undefined;

    // suggestion is string when he user presses Enter instead of selecting from dropdown
    if (typeof rawSuggestion === "string") {
      if (coordPattern.test(rawSuggestion)) {
        const matches = rawSuggestion.match(coordPattern);
        if (matches) {
          const coord1 = parseFloat(matches[1]);
          const coord2 = parseFloat(matches[2]);
          suggestion = {
            id: "coordinate-search",
            placeName: `${coord1}, ${coord2}`, // after selection the user needs to see what he initially inserted
            center: [coord1, coord2],
            isCoordinate: true,
          };
        }
      }
    } else {
      suggestion = rawSuggestion;
    }

    // suggestion = rawSuggestion as Suggestion;
    if (!suggestion) return;

    if (onViewStateChange) {
      const latitude = suggestion.center[0];
      const longitude = suggestion.center[1];
      onViewStateChange({ longitude, latitude, zoom: 14 });
    }

    if (handleOnResult) {
      handleOnResult(suggestion.placeName);
    }

    // Center map on the selected location
    if (mapRef?.current && suggestion.center) {
      mapRef.current.flyTo({
        // mapbox puts the longitude first and latitude second, users insert coords the opposite way
        center: [suggestion.center[1], suggestion.center[0]],
        zoom: 14,
      });
    }
  };

  return (
    <Autocomplete
      freeSolo
      open={open}
      onOpen={() => {
        if (input.length > 0) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      inputValue={input}
      onInputChange={(_, newValue) => {
        setInput(newValue);
        if (newValue.length > 0) setOpen(true);
      }}
      options={suggestions}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.placeName)}
      loading={loading}
      loadingText="Loading suggestions..."
      noOptionsText="No locations found"
      // called when user selects one option from dropdown
      onChange={(_, newValue) => {
        handleSelect(newValue);
      }}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          sx={{
            backgroundColor: "white",
            height: "35px",
            "& .MuiInputBase-root": {
              height: "100%",
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <React.Fragment key={option.id}>
          {coordinatesInItaly && !coordinatesOutOfRange && (
            <ListItem
              {...props}
              sx={{
                height: "45px", // Set the desired height
                "& .MuiListItemText-root": {
                  margin: 0, // Remove default margin
                },
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem", // Adjust font size if needed
                },
                "& .MuiListItemText-secondary": {
                  fontSize: "0.75rem", // Adjust font size if needed
                },
              }}
            >
              <Box sx={{ color: "text.secondary", marginBottom: "2px", marginRight: "5px" }}>
                {option.isCoordinate ? (
                  <MyLocationIcon fontSize="small" color="primary" />
                ) : (
                  <LocationOnIcon fontSize="small" />
                )}
              </Box>

              <ListItemText
                sx={{
                  height: "35px", // Set the desired height
                  "& .MuiListItemText-secondary": {
                    textOverflow: "ellipsis", // Add ellipsis for overflowing text
                    overflow: "hidden", // Hide overflowing content
                    whiteSpace: "nowrap", // Prevent text wrapping
                  },
                }}
                primary={option.isCoordinate ? "Search at these coordinates" : option.placeName.split(",")[0]}
                secondary={
                  option.isCoordinate
                    ? coordinatesReversed
                      ? // for the user the coordinates need to be displayed in the format he introduced them lat, lng
                        option.center.slice().reverse().join(", ")
                      : option.center.join(", ")
                    : option.placeName.split(",").slice(1).join(",").trim()
                }
                primaryTypographyProps={{
                  fontWeight: option.isCoordinate ? 500 : 400,
                }}
              />
            </ListItem>
          )}

          {/* if this is placed out of the renderOption function it will not be displayed */}
          {!coordinatesInItaly && (
            <Typography variant="body1" style={{ marginLeft: "10px" }}>
              Inserted coordinates are not in Italy
            </Typography>
          )}

          {option.id !== suggestions[suggestions.length - 1].id && <Divider />}
        </React.Fragment>
      )}
    />
  );
};
